// Parses `error.stack` to find the first user frame (the function that threw,
// not anything inside the logger). Never throws — a parse failure must not
// break the log call.

import type { OriginFrame } from './types'

const LOGGER_INTERNALS = ['/core/logging/', 'logger.ts', 'transports.ts', 'stackTrace.ts']

const isInternalFrame = (line: string): boolean =>
  LOGGER_INTERNALS.some((needle) => line.includes(needle))

const parseV8Frame = (line: string): OriginFrame | null => {
  const named = line.match(/^\s*at\s+(.+?)\s+\((.+?):(\d+):\d+\)\s*$/)
  if (named) {
    return { function: named[1], file: named[2], line: Number(named[3]) }
  }
  const anon = line.match(/^\s*at\s+(.+?):(\d+):\d+\s*$/)
  if (anon) {
    return { file: anon[1], line: Number(anon[2]) }
  }
  return null
}

const parseFirefoxFrame = (line: string): OriginFrame | null => {
  const named = line.match(/^(.+?)@(.+?):(\d+):\d+$/)
  if (named) {
    const fn = named[1] ?? ''
    return {
      function: fn.length > 0 ? fn : undefined,
      file: named[2],
      line: Number(named[3]),
    }
  }
  return null
}

export const parseOriginFromStack = (stack: string | undefined): OriginFrame | undefined => {
  if (!stack) return undefined
  const lines = stack.split('\n')
  for (const raw of lines) {
    const line = raw.trim()
    if (!line) continue
    if (line.startsWith('Error')) continue
    if (isInternalFrame(line)) continue

    const v8 = parseV8Frame(line)
    if (v8) return v8

    const ff = parseFirefoxFrame(line)
    if (ff) return ff
  }
  return undefined
}
